/**
 * Firestore service for patient-submitted contact-lens requests (Sprint 4.11).
 *
 * Collection path: `clinics/{clinicId}/lensOrderRequests/{requestId}`
 *
 * O paciente cria os docs via Cloud Function (`/submit-lens-request`); o
 * staff usa este serviço pra triagem (listar + transicionar status). A
 * conversão clínica de receita-de-óculos → Rx-de-lente acontece no
 * wizard existente em `/pedidos-lente` (`LensOrdersPage`); aprovar uma
 * solicitação aqui é um ACK do médico — o `LensOrder` confirmado é
 * criado depois pelo fluxo normal e referenciado via `resultingOrderId`
 * caso o staff queira amarrar.
 *
 * Invariantes:
 *   - status nasce 'PENDING' (gravado pelo backend, não permitido aqui)
 *   - transições válidas: PENDING → APPROVED | DECLINED | EXPIRED
 *   - APPROVED/DECLINED/EXPIRED são terminais
 *   - reviewedAt/reviewedBy são gravados pelo serviço (UI não passa)
 */

import { db } from './config';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  limit as qLimit,
} from 'firebase/firestore';
import type { LensOrderRequest, LensOrderRequestStatus } from '@/types/lc';
import { stripUndefined } from './utils';

const COLLECTION = 'lensOrderRequests';

function colRef(clinicId: string) {
  return collection(db, 'clinics', clinicId, COLLECTION);
}

function docRef(clinicId: string, id: string) {
  return doc(db, 'clinics', clinicId, COLLECTION, id);
}

const ALLOWED_TRANSITIONS: Record<LensOrderRequestStatus, LensOrderRequestStatus[]> = {
  PENDING: ['APPROVED', 'DECLINED', 'EXPIRED'],
  APPROVED: [],
  DECLINED: [],
  EXPIRED: [],
};

function assertTransition(
  from: LensOrderRequestStatus,
  to: LensOrderRequestStatus,
): void {
  const allowed = ALLOWED_TRANSITIONS[from] ?? [];
  if (!allowed.includes(to)) {
    throw new Error(
      `Transição de status inválida: ${from} → ${to}. ` +
      `Permitidas a partir de ${from}: ${allowed.length ? allowed.join(', ') : '(nenhuma — estado final)'}`,
    );
  }
}

export const LensOrderRequestService = {
  /** Lista todas as solicitações da clínica, mais recentes primeiro. */
  async listAll(
    clinicId: string,
    options: { max?: number } = {},
  ): Promise<LensOrderRequest[]> {
    const { max = 100 } = options;
    const q = query(
      colRef(clinicId),
      orderBy('createdAt', 'desc'),
      qLimit(max),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({
      id: d.id,
      ...(d.data() as Omit<LensOrderRequest, 'id'>),
    }));
  },

  /** Apenas solicitações PENDENTES (fila de triagem do staff). */
  async listPending(clinicId: string): Promise<LensOrderRequest[]> {
    const q = query(
      colRef(clinicId),
      where('status', '==', 'PENDING'),
      orderBy('createdAt', 'desc'),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({
      id: d.id,
      ...(d.data() as Omit<LensOrderRequest, 'id'>),
    }));
  },

  async getRequest(clinicId: string, id: string): Promise<LensOrderRequest | null> {
    const snap = await getDoc(docRef(clinicId, id));
    if (!snap.exists()) return null;
    return { id: snap.id, ...(snap.data() as Omit<LensOrderRequest, 'id'>) };
  },

  /**
   * Carrega ambas as solicitações que compartilham o mesmo pairRequestId
   * (criadas juntas quando o paciente escolhe "AMBOS" no portal). Útil pro
   * staff aprovar/recusar o par em uma operação só. Retorna {od, oe} —
   * cada lado pode estar null se o doc tiver sido deletado por algum motivo.
   */
  async getPair(
    clinicId: string,
    pairRequestId: string,
  ): Promise<{ od: LensOrderRequest | null; oe: LensOrderRequest | null }> {
    const q = query(
      colRef(clinicId),
      where('pairRequestId', '==', pairRequestId),
    );
    const snap = await getDocs(q);
    const result: { od: LensOrderRequest | null; oe: LensOrderRequest | null } = {
      od: null,
      oe: null,
    };
    for (const d of snap.docs) {
      const data = { id: d.id, ...(d.data() as Omit<LensOrderRequest, 'id'>) };
      if (data.eye === 'OD') result.od = data;
      else if (data.eye === 'OE') result.oe = data;
    }
    return result;
  },

  /**
   * Aprova a solicitação. `resultingOrderId` é opcional — quando o staff
   * já criou o `LensOrder` antes de marcar como aprovada, passa o id pra
   * amarrar os dois documentos. Caso contrário só marca o ACK.
   */
  async approve(
    clinicId: string,
    id: string,
    actorUid: string,
    resultingOrderId?: string,
  ): Promise<void> {
    const current = await this.getRequest(clinicId, id);
    if (!current) throw new Error('Solicitação de lente não encontrada');
    assertTransition(current.status, 'APPROVED');
    await updateDoc(
      docRef(clinicId, id),
      stripUndefined({
        status: 'APPROVED',
        reviewedAt: Date.now(),
        reviewedBy: actorUid,
        resultingOrderId,
      }),
    );
  },

  /**
   * Recusa a solicitação. `reason` é gravada em `notes` (campo já existe
   * no schema) prefixada com "[Recusa] " pra preservar qualquer observação
   * que o paciente tenha mandado. Mantém a observação original visível
   * no histórico do staff.
   */
  async decline(
    clinicId: string,
    id: string,
    actorUid: string,
    reason: string,
  ): Promise<void> {
    const current = await this.getRequest(clinicId, id);
    if (!current) throw new Error('Solicitação de lente não encontrada');
    assertTransition(current.status, 'DECLINED');

    const trimmed = reason.trim().slice(0, 500);
    const previousNotes = (current.notes || '').trim();
    const combinedNotes = previousNotes
      ? `[Recusa: ${trimmed}]\n${previousNotes}`
      : `[Recusa: ${trimmed}]`;

    await updateDoc(
      docRef(clinicId, id),
      stripUndefined({
        status: 'DECLINED',
        reviewedAt: Date.now(),
        reviewedBy: actorUid,
        notes: combinedNotes,
      }),
    );
  },
};
